import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Checkbox, Input } from '@heroui/react';
import PasswordRestrictions from '../../resetPassword/passwordRestriction';
import { ButtonDiv } from './signInSignUp';
import breakpoints from '../../../styling/breakpoints';
import keyCodes from '../../userDocuments/editDocument/editJournal/constants/keyCodes';
import CustomButton from './components/button';

const PassReq = styled.div`
  margin: 12px 0 0 4px;

  div {
    width: 100%;
    text-align: left;
    padding: 0;
    div {
      font-size: 12px;
      line-height: 10px;
      display: inline-flex;
    }
  }
`;
const InputWrapper = styled.div`
  grid-column: 4 / span 6;
  position: relative;
  display: flex;
  flex: 1;
  margin-top: 12px;
  margin-right: ${(props) => (props.side === 'left' ? '4px' : '0px')};
  margin-left: ${(props) => (props.side === 'right' ? '4px' : '0px')};
  @media ${breakpoints.mobileLg} {
    flex: 0;
    margin-left: 0px;
    margin-right: 0px;
  }
`;

const CheckboxContainer = styled.div`
  text-align: left;
  padding: 10px 0 0 0;
  margin-left: 2px;
  font-size: 11px;
`;

const WraperDiv = styled.div`
  display: flex;
  @media ${breakpoints.mobileLg} {
    flex-direction: column;
  }
`;

const SignUp = (props) => {
  const {
    handleSignUp,
    handleChange,
    isValidSignUpDetails,
    firstName,
    lastName,
    signUpEmail,
    signUpPassword,
    referrerId,
    repeatPassword,
    getReferrerIdFromQueryParam,
    isFirstNameValid,
    isLastNameValid,
    isSignUpEmailValid,
    showLoading,
    couponsAndDeals,
    handleCheckBox,
  } = props;
  const [isReferrerIdChanged, setReferrerIdChanged] = useState(false);
  useEffect(() => {
    const registerOnEnter = (event) => {
      if (event.keyCode === keyCodes.ENTER) {
        event.preventDefault();
        event.stopPropagation();
        handleSignUp();
      }
    };
    document.addEventListener('keydown', registerOnEnter, false);
    getReferrerIdFromQueryParam();
    setReferrerIdChanged(true);
    return () => {
      document.removeEventListener('keydown', registerOnEnter, false);
    };
  }, [isReferrerIdChanged, getReferrerIdFromQueryParam, handleSignUp]);
  const isValidDetails = isValidSignUpDetails();
  return (
    <>
      <div className="tabletMd:mt-[8px]">
        <WraperDiv>
          <InputWrapper side="left">
            <Input
              type="text"
              label="First Name"
              placeholder="Enter your first name"
              isRequired
              value={firstName}
              onChange={(e) => {
                handleChange(e, 'firstName');
              }}
              isInvalid={!isFirstNameValid}
              data-testid="firstName-signUp"
            />
          </InputWrapper>
          <InputWrapper side="right">
            <Input
              type="text"
              label="Last Name"
              placeholder="Enter your last name"
              isRequired
              value={lastName}
              onChange={(e) => {
                handleChange(e, 'lastName');
              }}
              isInvalid={!isLastNameValid}
              data-testid="lastName-signUp"
            />
          </InputWrapper>
        </WraperDiv>
        <InputWrapper>
          <Input
            type="email"
            label="Email"
            placeholder="Enter your email"
            isRequired
            value={signUpEmail}
            onChange={(e) => {
              handleChange(e, 'signUpEmail');
            }}
            isInvalid={!isSignUpEmailValid}
            data-testid="email-signUp"
          />
        </InputWrapper>
        <WraperDiv>
          <InputWrapper side="left">
            <Input
              type="password"
              label="Password"
              placeholder="Enter your password"
              isRequired
              value={signUpPassword}
              onChange={(e) => {
                handleChange(e, 'signUpPassword');
              }}
              data-testid="password-signUp"
            />
          </InputWrapper>

          <InputWrapper side="right">
            <Input
              type="password"
              label="Confirm Password"
              placeholder="Confirm your password"
              isRequired
              value={repeatPassword}
              onChange={(e) => {
                handleChange(e, 'repeatPassword');
              }}
              data-testid="confirmPassword-signUp"
            />
          </InputWrapper>
        </WraperDiv>
        <InputWrapper>
          <Input
            type="text"
            placeholder="Referrer ID(Optional)"
            value={referrerId}
            onChange={(e) => {
              handleChange(e, 'referrerId');
            }}
            data-testid="referrerId-signUp"
          />
        </InputWrapper>
      </div>
      {signUpPassword || repeatPassword ? (
        <PassReq>
          <PasswordRestrictions
            signUpPassword={signUpPassword}
            repeatPassword={repeatPassword}
          />
        </PassReq>
      ) : null}
      <CheckboxContainer className="mb-[4px] mt-[0px] tabletSm:mt-[4px] tabletSm:mb-0 tabletMd:mt-[8px]">
        <Checkbox
          size="sm"
          type="checkbox"
          isSelected={couponsAndDeals}
          onChange={(e) => {
            handleCheckBox(e, 'couponsAndDeals');
          }}
          classNames={{
            icon: 'opacity-1',
            label: 'text-[12px] tabletMd:text-[14px]',
          }}
        >
          Subscribe to stay updated with new features and offers!
        </Checkbox>
      </CheckboxContainer>
      <ButtonDiv className="!mb-[4px]">
        <CustomButton
          isLoading={showLoading}
          onClick={handleSignUp}
          isDisabled={!isValidDetails || showLoading}
          data-testid="button-signUp"
          aria-label="button-signUp"
          variant="solid"
          color="primary"
        >
          Register
        </CustomButton>
      </ButtonDiv>
    </>
  );
};

SignUp.propTypes = {
  handleChange: PropTypes.func,
  handleSignUp: PropTypes.func,
  isValidSignUpDetails: PropTypes.func,
  getReferrerIdFromQueryParam: PropTypes.func,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  signUpEmail: PropTypes.string,
  signUpPassword: PropTypes.string,
  referrerId: PropTypes.string,
  repeatPassword: PropTypes.string,
  isFirstNameValid: PropTypes.bool,
  isLastNameValid: PropTypes.bool,
  isSignUpEmailValid: PropTypes.bool,
  showLoading: PropTypes.bool,
  couponsAndDeals: PropTypes.bool,
  handleCheckBox: PropTypes.func,
};

SignUp.defaultProps = {
  handleChange: () => {},
  handleSignUp: () => {},
  isValidSignUpDetails: () => {},
  getReferrerIdFromQueryParam: () => {},
  firstName: '',
  lastName: '',
  signUpEmail: '',
  signUpPassword: '',
  referrerId: '',
  repeatPassword: '',
  isFirstNameValid: true,
  isLastNameValid: true,
  isSignUpEmailValid: true,
  showLoading: false,
  couponsAndDeals: false,
  handleCheckBox: () => {},
};

export default SignUp;
